/**
 * Client portfolios API (HP; backend `routers/portfolios.py`, gated `portfolios.manage`).
 *
 * Upload the custodian's daily positions file (preview, then save) and view one stored
 * portfolio on one date, grouped by a lens of the firm-wide active classification view.
 * Amounts arrive as decimal STRINGS at full precision.
 */
import { api, ApiError } from './client';

/**
 * Banks arc step 2 (0113): a portfolio is bank + number. `ref` ("<bank id>:<number>", or
 * "none:<number>" when the bank was not recorded) is how the API names it; `label`
 * ("BNP · 000123456") is how the screens show it.
 */
export interface PortfolioRef {
  ref: string; bank_mnemonic: string | null; label: string;
  /** Banks arc step 4 (0115): the agreement it is linked to, if any. */
  agreement_id?: string | null; agreement_label?: string | null;
}

export interface UploadPortfolio {
  portfolio_number: string; portfolio_key: string; positions: number; cash_lines: number;
  /** HP-L: Category LOANS lines (not counted as positions). */
  loan_lines?: number;
  /** 3a: the Net value in the portfolio's reference currency (as the file gives it), and in EUR
   *  at the file's own rates (null: the file gives no rate for that currency). */
  ref_currency?: string | null; value_ref?: string | null;
  value_eur: string | null; new_securities: number; without_isin: number; already_stored: boolean;
}
export interface NewSecurity {
  isin: string; currency: string; name: string; in_master_other_currencies: string[];
  portfolios: string[];   // 6-digit keys
}
export interface RowNote { row: number | null; portfolio_key: string | null; reason: string }
export interface UploadReport {
  file_name: string; size_bytes: number; sha256: string; as_of: string; data_rows: number;
  /** 0113: the bank chosen for the upload. */
  bank_mnemonic?: string | null;
  /** 0114: which layout read the file ("BNP's layout, last changed …"). */
  layout_note?: string | null;
  portfolios: UploadPortfolio[]; positions: number; cash_lines: number; loan_lines?: number;
  new_securities: NewSecurity[]; without_isin: RowNote[];
  /** HP-F: rows where an informational column held something unreadable (kept empty). */
  unreadable?: RowNote[];
  replaces: string[]; blocking: RowNote[]; can_save: boolean;
  saved: boolean; upload_id: string | null; securities_added: number; replaced: string[];
}
export interface UploadRecord {
  id: string; as_of: string; file_name: string; uploaded_at: string; uploaded_by: string | null;
  /** 0113: the upload's bank; null = bank not recorded. */
  bank_mnemonic?: string | null;
  portfolios: number; positions: number; replaced_portfolios: number; securities_added: number;
  rolled_back_at: string | null; rolled_back_by: string | null;
}
/** What rolling an upload back does (preview), or did (after the POST). */
export interface RollbackPlan {
  upload_id: string; as_of: string; file_name: string; uploaded_at: string; lines: number;
  portfolios: { portfolio_number: string; portfolio_key: string; as_of: string; in_force: boolean; outcome: string }[];
  securities_removed: { id: string; isin: string; currency: string; name: string }[];
  securities_kept: { id: string; isin: string; currency: string; name: string; reason: string | null }[];
  rolled_back: boolean;
}
export interface PortfolioMatch extends PortfolioRef { portfolio_number: string; portfolio_key: string; dates: string[] }
export interface HoldingLine {
  line_no: number; is_cash: boolean; category: string; security_id: string | null; name: string;
  /** HP-L: a loan line (Category LOANS); a cash or loan line's account code (Denomination). */
  is_loan?: boolean; account_code?: string | null;
  isin: string | null; currency: string; quantity: string; price: string | null; value_pos: string;
  /** Net value and Total P&L in EUR, from the line's reference currency (3a; null = no rate). */
  fx: string | null; value_eur: string | null; accrued: string; avg_price: string | null;
  total_cost: string | null; total_pnl: string | null; pct: string | null; path: string[];
  /** HP-F (0109): the file's other columns; null on lines uploaded before 0109. */
  ref_currency?: string | null; interest_rate?: string | null; pct_portfolio?: string | null;
  buy_date?: string | null; purchase_currency?: string | null; capital_pnl?: string | null;
  currency_pnl?: string | null; total_pnl_pct?: string | null; subtype?: string | null;
  strike_price?: string | null; avg_buy_fx?: string | null;
  /** Every lens of the view: dimension code → path (top tier first). */
  classifications: Record<string, string[]>;
  /** The same paths' taxonomy sort orders (HP-V: the page groups by any lens tier in this order). */
  orders: Record<string, number[]>;
  non_classified: boolean;
}
export interface HoldingGroup { label: string; value_eur: string | null; pct: string | null; lines: HoldingLine[] }
export interface PortfolioLens { code: string; name: string; tiers: { level: number; name: string }[] }
export interface PortfolioView extends PortfolioRef {
  portfolio_number: string; portfolio_key: string; as_of: string; dates: string[];
  view: { id: string; name: string } | null;
  lenses: PortfolioLens[]; lens: string | null;   // the grouping lens code, or "none"
  /** 3a: the file's reference currency for this portfolio. */
  ref_currency?: string | null;
  /** Net (every line); HP-L: gross = every line but the loans, the base of every %. In EUR. */
  total_value_eur: string | null; gross_value_eur?: string | null;
  positions: number; non_classified: number; groups: HoldingGroup[];
}
// ── HP-C: several portfolios side by side ──────────────────────────────────────
/** One portfolio's figures on a consolidated row, as stored. */
export interface ConsolidatedLine {
  /** value_eur / total_pnl: in EUR from the line's reference currency (3a); null = no rate. */
  quantity: string; price: string | null; value_pos: string; fx: string | null; value_eur: string | null;
  /** 3a: value / Total P&L in the line's reference currency (value: accrued interest included, ACC-1). */
  value_ref?: string | null; total_pnl_ref?: string | null;
  /** ACC-1: the value's two parts in the reference currency: the file's Net value (clean) and its
   *  Accrued interest (the file gives both in the reference currency). */
  clean_ref?: string | null; accrued_ref?: string | null;
  accrued: string; avg_price: string | null; total_cost: string | null; total_pnl: string | null;
  /** COST-SRC B (0127): cost / P&L come from a cost entered in the app (the custodian gives none). */
  cost_entered?: boolean; cost_note?: string | null;
  purchase_currency: string | null; pct: string | null; category: string;
  ref_currency: string | null; interest_rate: string | null; pct_portfolio: string | null;
  buy_date: string | null; capital_pnl: string | null; currency_pnl: string | null;
  total_pnl_pct: string | null; subtype: string | null; strike_price: string | null; avg_buy_fx: string | null;
}
export interface ConsolidatedRow {
  key: string; is_cash: boolean; name: string; isin: string | null; currency: string;
  /** HP-L: a loan row; a cash or loan row's account code (Denomination). */
  is_loan?: boolean; account_code?: string | null;
  /** TQ1: from the Securities Master (null for a line not in it). */
  bb_ticker?: string | null; figi?: string | null;
  /** TRADE-1: Approved in Securities (T5), and the minimum lot / increment (TQ6). */
  authorized?: boolean; min_piece?: string | null; min_increment?: string | null;
  security_id: string | null; classifications: Record<string, string[]>; orders: Record<string, number[]>;
  /** Keyed by the portfolio's ref (0113). */
  non_classified: boolean; by_portfolio: Record<string, ConsolidatedLine>;
}
export interface ConsolidatedPortfolio extends PortfolioRef {
  portfolio_number: string; portfolio_key: string;
  /** null: no positions on the view's date (flagged, Pedro Q2). */
  used_date: string | null; current: boolean; latest_before: string | null;
  /** 3a: the file's reference currency for this portfolio. */
  ref_currency?: string | null;
  /** Net (every line). HP-L: gross = every line but the loans, the base of every %. */
  total_value_eur: string | null; gross_value_eur?: string | null;
  /** 3a: the same two in the portfolio's reference currency. */
  total_value_ref?: string | null; gross_value_ref?: string | null;
}
export interface ConsolidatedView {
  as_of: string; dates: string[]; view: { id: string; name: string } | null; lenses: PortfolioLens[];
  portfolios: ConsolidatedPortfolio[];
  /** EUR per 1 unit of each currency held on the view's date (all custodians pooled). */
  rates: Record<string, string>;
  /** TOT-DASH: each custodian's rates (by bank id; 'none' = no bank), with any chosen fallback. */
  rates_by_bank?: Record<string, Record<string, string>>;
  /** TOT-DASH: rates in use that are not the custodian's own for that date. */
  rate_sources?: RateSource[];
  /** TOT-DASH: currencies a custodian gives no rate for that date, with the fallbacks that exist. */
  missing_rates?: MissingRate[];
  rows: ConsolidatedRow[];
}
export interface RateSource {
  bank: string; bank_mnemonic: string | null; currency: string; date: string; rate: string;
  source: 'last' | 'other' | 'typed'; rate_date?: string | null;
  from_bank?: string | null; from_bank_mnemonic?: string | null;
}
export interface MissingRate {
  bank: string; bank_mnemonic: string | null; currency: string; date: string; portfolios: string[];
  last: { date: string; rate: string } | null;
  others: { bank: string; bank_mnemonic: string | null; rate: string }[];
}

/** The portfolio picker's rows; `id` is the portfolio's ref (bank + number, 0113). */
export interface PortfolioSearchItem {
  id: string; bank_mnemonic: string | null; label: string;
  portfolio_number: string; portfolio_key: string; latest_date: string; dates: number;
  agreement_id?: string | null; agreement_label?: string | null;
}
export interface HeldSecurity { id: string; isin: string; currency: string; name: string; portfolios: number }
export interface SecurityHoldingLine extends PortfolioRef {
  portfolio_number: string; portfolio_key: string; quantity: string; price: string | null;
  value_pos: string; fx: string | null; value_eur: string | null; avg_price: string | null;
  total_cost: string | null; total_pnl: string | null; pct_of_portfolio: string | null;
  portfolio_value_eur: string | null;
  /** 3a: the portfolio's reference currency (the ladder's currency for this line). */
  ref_currency?: string | null;
  /** COST-SRC B (0127): cost / P&L from a cost entered in the app. */
  cost_entered?: boolean; cost_note?: string | null;
}
export interface SecurityAcrossPortfolios {
  security: { id: string; isin: string; currency: string; name: string };
  as_of: string; dates: string[]; portfolios: number; total_quantity: string; total_value_eur: string | null;
  /** SEC-LADDER: the same rate ladder as View portfolio (shown in EUR). */
  left_out?: string[]; rate_sources?: RateSource[]; missing_rates?: MissingRate[];
  lines: SecurityHoldingLine[];
}
/** COST-FIX (C-411): a security line without cost information from the custodian (latest positions). */
export interface NoCostLine extends PortfolioRef {
  portfolio_number: string; as_of: string; security_id: string | null; name: string; isin: string | null;
  currency: string; quantity: string | null; value_ref: string | null; ref_currency: string | null;
  /** COST-SRC B (0127): the cost entered in the app for this line, and what it gives. */
  bank_id?: string | null; unit_factor?: string; entry?: CostEntry | null;
  total_cost?: string | null; total_pnl_ref?: string | null;
}
export interface CostEntry {
  id: string; avg_price: string; avg_buy_fx: string; note: string;
  updated_at: string | null; updated_by_name: string | null;
}
export interface NoCostList { items: NoCostLine[]; portfolios: number }
interface PageOf<T> { items: T[]; total: number; limit: number; offset: number }

/** A ref's bank id (null when "none", the bank not recorded) and number. */
export function splitRef(ref: string): { bank: string | null; number: string } {
  const i = ref.indexOf(':');
  if (i < 0) return { bank: null, number: ref };
  const bank = ref.slice(0, i);
  return { bank: bank === 'none' ? null : bank, number: ref.slice(i + 1) };
}

async function upload(file: File, asOf: string, bankId: string, mode: 'preview' | 'commit',
                      confirmReplace: boolean): Promise<UploadReport> {
  const form = new FormData();
  form.append('file', file);
  form.append('as_of', asOf);
  form.append('bank_id', bankId);
  form.append('mode', mode);
  form.append('confirm_replace', confirmReplace ? 'true' : 'false');
  const res = await fetch('/portfolios/upload', { method: 'POST', credentials: 'include', body: form });
  if (!res.ok) {
    const j = await res.json().catch(() => null);
    const detail = (j && typeof j.detail === 'string' && j.detail) || 'The positions file could not be processed.';
    throw new ApiError(res.status, detail);
  }
  return res.json();
}

export const portfoliosApi = {
  preview: (file: File, asOf: string, bankId: string) => upload(file, asOf, bankId, 'preview', false),
  save: (file: File, asOf: string, bankId: string, confirmReplace: boolean) =>
    upload(file, asOf, bankId, 'commit', confirmReplace),
  uploads: () => api.get<UploadRecord[]>('/portfolios/uploads'),
  rollbackPlan: (id: string) => api.get<RollbackPlan>(`/portfolios/uploads/${encodeURIComponent(id)}/rollback`),
  rollback: (id: string) => api.post<RollbackPlan>(`/portfolios/uploads/${encodeURIComponent(id)}/rollback`, {}),
  lookup: (key: string) => api.get<PortfolioMatch[]>(`/portfolios/lookup?key=${encodeURIComponent(key)}`),
  /** One portfolio by its ref (bank + number, 0113). */
  get: (ref: string, opts: { asOf?: string; lens?: string | null; view?: string | null } = {}) => {
    const { bank, number } = splitRef(ref);
    const q = new URLSearchParams();
    if (bank) q.set('bank_id', bank);
    if (opts.asOf) q.set('as_of', opts.asOf);
    if (opts.lens) q.set('lens', opts.lens);
    if (opts.view) q.set('view', opts.view);
    const s = q.toString();
    return api.get<PortfolioView>(`/portfolios/${encodeURIComponent(number)}${s ? `?${s}` : ''}`);
  },
  /** HP-C: several portfolios (refs) on one date; `substitutes` = ref → an earlier date to use. */
  consolidated: (refs: string[], opts: {
    asOf?: string; view?: string | null; substitutes?: Record<string, string>;
    /** TOT-DASH: "bank:CCY" → 'last' | 'from:<bank>' | a typed rate (EUR per 1 unit). */
    rateChoices?: Record<string, string>; show?: string;
  } = {}) => {
    const q = new URLSearchParams({ portfolios: refs.join(',') });
    if (opts.asOf) q.set('as_of', opts.asOf);
    if (opts.view) q.set('view', opts.view);
    const subs = Object.entries(opts.substitutes ?? {}).map(([r, d]) => `${r}@${d}`).join(',');
    if (subs) q.set('substitute', subs);
    const rc = Object.entries(opts.rateChoices ?? {}).map(([k, v]) => `${k}:${v}`).join(',');
    if (rc) q.set('rate', rc);
    if (opts.show) q.set('show', opts.show);
    return api.get<ConsolidatedView>(`/portfolios/consolidated?${q.toString()}`);
  },
  search: (q: string) =>
    api.get<PageOf<PortfolioSearchItem>>(`/portfolios/search?q=${encodeURIComponent(q)}&limit=25`),
  heldSecurities: (q: string) =>
    api.get<PageOf<HeldSecurity>>(`/portfolios/securities?q=${encodeURIComponent(q)}&limit=25`),
  /** COST-FIX (C-411): every security line without cost information, across all portfolios. */
  noCost: () => api.get<NoCostList>('/portfolios/no-cost'),
  /** COST-SRC B (0127): enter / change a cost the custodian does not give (`portfolios.cost`). */
  saveCost: (body: { portfolio: string; isin: string; currency: string; avg_price: string; avg_buy_fx: string; note: string }) =>
    api.put<CostEntry>('/portfolios/cost-entries', body),
  removeCost: (id: string) => api.delete<void>(`/portfolios/cost-entries/${encodeURIComponent(id)}`),
  bySecurity: (securityId: string, asOf?: string, rateChoices?: Record<string, string>) => {
    const q = new URLSearchParams();
    if (asOf) q.set('as_of', asOf);
    const rc = Object.entries(rateChoices ?? {}).map(([k, v]) => `${k}:${v}`).join(',');
    if (rc) q.set('rate', rc);
    const qs = q.toString();
    return api.get<SecurityAcrossPortfolios>(
      `/portfolios/by-security/${encodeURIComponent(securityId)}${qs ? `?${qs}` : ''}`);
  },
};
